/**
 * ApprovalBandsField - composite editor for approval bands.
 *
 * Rows of (currency, over-amount, approver). A genuinely composite
 * widget embedded in the generated form via `type: 'custom'` (per the
 * Generated Form Rules); every control is a shared component. Currency
 * options arrive from the backend (tenant data), approver options from
 * the organisation's roles and members - zero hardcoded values.
 */

import { X } from 'lucide-react'
import { Button } from '@/shared/components/ui/button'
import { Input } from '@/shared/components/ui/input'
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui/select'
import type { ApprovalPolicyOption } from '@/admin/hooks/use-approval-policy'

/** Draft band as edited on the page. `approver` is encoded as
 * `role:<name>` / `person:<email>` / '' so a single Select carries both
 * kinds; decoded back to {kind, value} at submit time. */
export interface ApprovalBandDraft {
  currency: string
  amount: number | ''
  approver: string
}

export interface ApproverItem {
  /** Encoded select value: `role:<name>` or `person:<email>`. */
  value: string
  label: string
}

interface ApprovalBandsFieldProps {
  value: ApprovalBandDraft[]
  onChange: (next: ApprovalBandDraft[]) => void
  currencyOptions: ApprovalPolicyOption[]
  roleItems: ApproverItem[]
  memberItems: ApproverItem[]
  /** True when assignment strategy leaves claims unassigned. */
  approverDisabled: boolean
  error?: string
}

const GRID = 'grid grid-cols-[96px_110px_minmax(0,1fr)_36px] items-center gap-2.5'

export function ApprovalBandsField({
  value,
  onChange,
  currencyOptions,
  roleItems,
  memberItems,
  approverDisabled,
  error,
}: ApprovalBandsFieldProps) {
  // Resolve the trigger label ourselves: the shared Select derives labels
  // from rendered items, which are not there yet while the role/member
  // queries load - without this the raw encoded value (`person:<email>`)
  // leaks into the trigger. Fallback strips the kind prefix so a value
  // outside the lists still reads as the email / role name.
  const approverLabel = (encoded: string): string => {
    const match =
      roleItems.find((item) => item.value === encoded) ??
      memberItems.find((item) => item.value === encoded)
    if (match) return match.label
    const separator = encoded.indexOf(':')
    return separator > 0 ? encoded.slice(separator + 1) : encoded
  }

  const updateBand = (index: number, patch: Partial<ApprovalBandDraft>) => {
    onChange(value.map((band, i) => (i === index ? { ...band, ...patch } : band)))
  }

  const removeBand = (index: number) => {
    onChange(value.filter((_, i) => i !== index))
  }

  const addBand = () => {
    const used = new Set(value.map((band) => band.currency))
    const nextCurrency =
      currencyOptions.find((option) => !used.has(option.value))?.value ?? ''
    onChange([...value, { currency: nextCurrency, amount: '', approver: '' }])
  }

  return (
    <div className="col-span-2 flex flex-col">
      <div className={`${GRID} pb-2`}>
        <span className="text-[11px] uppercase tracking-[0.14em] text-[var(--ink-3)]">
          Currency
        </span>
        <span className="text-[11px] uppercase tracking-[0.14em] text-[var(--ink-3)]">
          Over
        </span>
        <span className="text-[11px] uppercase tracking-[0.14em] text-[var(--ink-3)]">
          Approved by
        </span>
        <span />
      </div>

      {value.length === 0 && (
        <p className="border-t-[0.5px] border-[var(--rule)] py-4 text-[13px] text-[var(--ink-3)]">
          No bands yet - every claim pays without approval. Add a band to
          require sign-off above an amount.
        </p>
      )}

      {value.map((band, index) => (
        <div
          key={index}
          className={`${GRID} border-t-[0.5px] border-[var(--rule)] py-2.5`}
        >
          <Select
            value={band.currency}
            onValueChange={(next) => updateBand(index, { currency: next ?? '' })}
          >
            <SelectTrigger aria-label="Currency">
              <SelectValue placeholder="..." />
            </SelectTrigger>
            <SelectContent>
              {currencyOptions.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Input
            type="number"
            min={0}
            step="1"
            aria-label="Over amount"
            value={band.amount === '' ? '' : String(band.amount)}
            onChange={(event) => {
              const raw = event.target.value
              updateBand(index, {
                amount: raw === '' ? '' : Number(raw),
              })
            }}
          />

          <Select
            value={band.approver || undefined}
            disabled={approverDisabled}
            onValueChange={(next) => updateBand(index, { approver: next ?? '' })}
          >
            <SelectTrigger aria-label="Approved by">
              <SelectValue
                placeholder={approverDisabled ? 'Unassigned - queue only' : 'Pick an approver'}
              >
                {band.approver ? approverLabel(band.approver) : undefined}
              </SelectValue>
            </SelectTrigger>
            <SelectContent>
              {roleItems.length > 0 && (
                <SelectGroup>
                  <SelectLabel>Roles</SelectLabel>
                  {roleItems.map((item) => (
                    <SelectItem key={item.value} value={item.value}>
                      {item.label}
                    </SelectItem>
                  ))}
                </SelectGroup>
              )}
              {memberItems.length > 0 && (
                <SelectGroup>
                  <SelectLabel>People</SelectLabel>
                  {memberItems.map((item) => (
                    <SelectItem key={item.value} value={item.value}>
                      {item.label}
                    </SelectItem>
                  ))}
                </SelectGroup>
              )}
            </SelectContent>
          </Select>

          <Button
            type="button"
            variant="outline"
            size="icon-sm"
            aria-label="Remove band"
            onClick={() => removeBand(index)}
            className="text-[var(--ink-3)]"
          >
            <X size={14} />
          </Button>
        </div>
      ))}

      <div className="flex items-center gap-3.5 border-t-[0.5px] border-[var(--rule)] pt-3.5">
        <Button type="button" variant="outline" size="xs" onClick={addBand}>
          + Add band
        </Button>
        <span className="text-[12px] text-[var(--ink-3)]">
          One band per currency. The first band also covers claims with no
          currency captured.
        </span>
      </div>

      {error && (
        <p className="pt-2 text-[11px] text-[var(--status-error)]">{error}</p>
      )}
    </div>
  )
}
